/**
 * Anti-Gaming / Integrity Layer
 *
 * Validates strength_event entries before they're accepted.
 * Catches absurd lifts, suspicious patterns, and gaming attempts.
 *
 * Three tiers:
 *   1. Hard Reject — physically impossible, blocked outright
 *   2. Soft Flag   — suspicious but plausible, marked for review
 *   3. Clean       — passes all checks
 */

import { createLogger } from './logger';

const log = createLogger('ANTI_GAMING');

// ────────────────────────────────────────────
// World records (in lbs) as absolute ceilings
// We use 110% of the all-time record as the hard cap
// ────────────────────────────────────────────
const WORLD_RECORDS_LBS: Record<string, number> = {
  'bench-press': 782,       // Julius Maddox
  'back-squat': 1311,       // Ray Williams (wrapped)
  'deadlift': 1105,         // Danny Grigsby
  'overhead-press': 530,    // Zydrunas Savickas (log press ~)
  'weighted-pull-up': 250,  // added weight (not bodyweight)
  'dead-hang': 300,          // world record ~slightly over 16min; 300lb equiv placeholder
  'farmers-carry': 500,     // per hand, strongman
  'barbell-row': 600,       // estimated max
  'front-squat': 800,       // estimated max
};

const HARD_CAP_MULTIPLIER = 1.1; // 110% of world record

// ────────────────────────────────────────────
// Bodyweight-relative caps (soft flags)
// If someone claims to bench 5x bodyweight, flag it
// ────────────────────────────────────────────
const BW_RATIO_SOFT_FLAG: Record<string, number> = {
  'bench-press': 3.0,
  'back-squat': 4.0,
  'deadlift': 4.5,
  'overhead-press': 2.5,
  'weighted-pull-up': 2.0,  // added weight relative to BW
  'dead-hang': 5.0,          // seconds, so ratio doesn't apply the same way; high ceiling
  'farmers-carry': 3.0,
  'barbell-row': 3.0,
  'front-squat': 3.5,
};

// ────────────────────────────────────────────
// Rep sanity: max reps per set for flagging
// ────────────────────────────────────────────
const MAX_REPS_HARD = 200;  // no set has 200+ reps of a barbell lift
const MAX_REPS_SOFT = 50;   // flag anything over 50 reps

// ────────────────────────────────────────────
// Velocity check: max weight gain per week (lbs)
// If someone's bench jumps 100lbs in a week, flag it
// ────────────────────────────────────────────
const MAX_WEEKLY_GAIN_LBS: Record<string, number> = {
  'bench-press': 30,
  'back-squat': 40,
  'deadlift': 40,
  'overhead-press': 25,
  'weighted-pull-up': 25,
  'dead-hang': 20,           // max gain per session in seconds (reasonable daily improvement)
  'farmers-carry': 50,
  'barbell-row': 30,
  'front-squat': 35,
};

// ────────────────────────────────────────────
// Types
// ────────────────────────────────────────────

export type IntegrityVerdict = 'clean' | 'soft_flag' | 'hard_reject';

export interface IntegrityFlag {
  code: string;
  message: string;
  severity: 'hard' | 'soft';
}

export interface IntegrityResult {
  verdict: IntegrityVerdict;
  flags: IntegrityFlag[];
  /** Suggested quality_tier override (lower = less trusted) */
  qualityTier: number;
}

export interface LiftEntry {
  liftKey: string;
  weight: number;
  weightUnit: string;
  reps: number;
  bodyweightLbs?: number;
}

export interface PriorLift {
  liftKey: string;
  weight: number;
  weightUnit: string;
  performedAt: string;
}

// ────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────

function toLbs(weight: number, unit: string): number {
  return unit === 'kg' ? weight * 2.20462 : weight;
}

// ────────────────────────────────────────────
// Core validation
// ────────────────────────────────────────────

/**
 * Validate a single lift entry against integrity rules.
 * @param entry - The lift being submitted
 * @param priorLifts - Recent lifts for the same lift_key (for velocity check)
 */
export function validateLift(
  entry: LiftEntry,
  priorLifts: PriorLift[] = [],
): IntegrityResult {
  const flags: IntegrityFlag[] = [];
  const weightLbs = toLbs(entry.weight, entry.weightUnit);

  // ── 1. Negative / zero weight ──
  if (entry.weight <= 0) {
    flags.push({ code: 'ZERO_WEIGHT', message: 'Weight must be positive', severity: 'hard' });
  }

  // ── 2. Negative / zero reps ──
  if (entry.reps <= 0) {
    flags.push({ code: 'ZERO_REPS', message: 'Reps must be positive', severity: 'hard' });
  }

  // ── 3. World record hard cap ──
  const worldRecord = WORLD_RECORDS_LBS[entry.liftKey];
  if (worldRecord && weightLbs > worldRecord * HARD_CAP_MULTIPLIER) {
    flags.push({
      code: 'EXCEEDS_WORLD_RECORD',
      message: `${Math.round(weightLbs)}lbs exceeds world record ceiling (${Math.round(worldRecord * HARD_CAP_MULTIPLIER)}lbs)`,
      severity: 'hard',
    });
  }

  // ── 4. Rep count hard cap ──
  if (entry.reps > MAX_REPS_HARD) {
    flags.push({
      code: 'ABSURD_REPS',
      message: `${entry.reps} reps exceeds maximum (${MAX_REPS_HARD})`,
      severity: 'hard',
    });
  }

  // ── 5. Rep count soft flag ──
  if (entry.reps > MAX_REPS_SOFT && entry.reps <= MAX_REPS_HARD) {
    flags.push({
      code: 'HIGH_REPS',
      message: `${entry.reps} reps is unusually high`,
      severity: 'soft',
    });
  }

  // ── 6. Bodyweight ratio soft flag ──
  if (entry.bodyweightLbs && entry.bodyweightLbs > 0) {
    const bwRatio = weightLbs / entry.bodyweightLbs;
    const softCap = BW_RATIO_SOFT_FLAG[entry.liftKey];
    if (softCap && bwRatio > softCap) {
      flags.push({
        code: 'HIGH_BW_RATIO',
        message: `${bwRatio.toFixed(1)}x bodyweight exceeds typical range (${softCap}x)`,
        severity: 'soft',
      });
    }
  }

  // ── 7. Velocity check (gain over time, 30-day window) ──
  if (priorLifts.length > 0) {
    const maxWeeklyGain = MAX_WEEKLY_GAIN_LBS[entry.liftKey] ?? 40;
    const now = Date.now();
    const thirtyDaysAgo = now - 30 * 24 * 3600 * 1000;

    // Sort all priors by date descending, get the most recent
    const sortedPriors = [...priorLifts].sort(
      (a, b) => new Date(b.performedAt).getTime() - new Date(a.performedAt).getTime()
    );
    const mostRecentPrior = sortedPriors[0];
    const priorTimestamp = new Date(mostRecentPrior.performedAt).getTime();
    const bestPriorLbs = toLbs(mostRecentPrior.weight, mostRecentPrior.weightUnit);
    const gain = weightLbs - bestPriorLbs;

    if (gain > 0) {
      if (priorTimestamp > thirtyDaysAgo) {
        // Within 30-day window: scale max gain by weeks elapsed
        const weeksElapsed = Math.max(1, (now - priorTimestamp) / (7 * 24 * 3600 * 1000));
        const scaledMaxGain = maxWeeklyGain * Math.ceil(weeksElapsed);
        if (gain > scaledMaxGain) {
          flags.push({
            code: 'RAPID_GAIN',
            message: `+${Math.round(gain)}lbs in ${Math.round(weeksElapsed)} week(s) (max expected: ${scaledMaxGain}lbs)`,
            severity: 'soft',
          });
        }
      } else {
        // Prior is older than 30 days: still check for extreme gains (3× monthly max)
        const monthlyMax = maxWeeklyGain * 4; // ~4 weeks
        const extremeMax = monthlyMax * 3;    // 3 months of max gains
        if (gain > extremeMax) {
          flags.push({
            code: 'EXTREME_GAIN_AFTER_ABSENCE',
            message: `+${Math.round(gain)}lbs after extended absence (max plausible: ${extremeMax}lbs)`,
            severity: 'soft',
          });
        }
      }
    }
  }

  // ── Verdict ──
  const hasHard = flags.some(f => f.severity === 'hard');
  const hasSoft = flags.some(f => f.severity === 'soft');

  const verdict: IntegrityVerdict = hasHard ? 'hard_reject' : hasSoft ? 'soft_flag' : 'clean';
  const qualityTier = hasHard ? 0 : hasSoft ? 1 : 2;

  if (flags.length > 0) {
    log.warn('Integrity flags', { liftKey: entry.liftKey, weight: entry.weight, reps: entry.reps, verdict, flags });
  }

  return { verdict, flags, qualityTier };
}

// ────────────────────────────────────────────
// Bodyweight velocity check
// ────────────────────────────────────────────

/** Maximum bodyweight change per 30-day period (lbs) */
const MAX_BW_CHANGE_30D_LBS = 15;

export interface BwChangeResult {
  allowed: boolean;
  flag: IntegrityFlag | null;
}

/**
 * Validate a bodyweight change against velocity limits.
 * Call this when the user updates their bodyweight in their profile.
 */
export function validateBodyweightChange(
  newBwLbs: number,
  previousBwLbs: number,
  lastChangedAt: string | null,
): BwChangeResult {
  if (!previousBwLbs || previousBwLbs <= 0) {
    return { allowed: true, flag: null }; // First time setting BW
  }

  const changeLbs = Math.abs(newBwLbs - previousBwLbs);

  // If the last change was within 30 days and the total change exceeds threshold, flag it
  if (lastChangedAt) {
    const daysSinceChange = (Date.now() - new Date(lastChangedAt).getTime()) / (1000 * 60 * 60 * 24);
    if (daysSinceChange < 30 && changeLbs > MAX_BW_CHANGE_30D_LBS) {
      return {
        allowed: false,
        flag: {
          code: 'RAPID_BW_CHANGE',
          message: `Bodyweight change of ${Math.round(changeLbs)}lbs in ${Math.round(daysSinceChange)} days exceeds limit (${MAX_BW_CHANGE_30D_LBS}lbs/30 days). Contact support if this is accurate.`,
          severity: 'soft',
        },
      };
    }
  }

  // Absolute sanity: no single change > 50lbs ever
  if (changeLbs > 50) {
    return {
      allowed: false,
      flag: {
        code: 'EXTREME_BW_CHANGE',
        message: `Bodyweight change of ${Math.round(changeLbs)}lbs is too large. Contact support if this is accurate.`,
        severity: 'hard',
      },
    };
  }

  return { allowed: true, flag: null };
}

/**
 * Validate an entire workout's worth of lift entries.
 * Returns per-lift results + overall verdict.
 */
export function validateWorkout(
  entries: LiftEntry[],
  priorLiftsByKey: Record<string, PriorLift[]> = {},
): { overall: IntegrityVerdict; results: Map<number, IntegrityResult> } {
  let overall: IntegrityVerdict = 'clean';
  const results = new Map<number, IntegrityResult>();

  for (let i = 0; i < entries.length; i++) {
    const entry = entries[i];
    const priors = priorLiftsByKey[entry.liftKey] || [];
    const result = validateLift(entry, priors);
    results.set(i, result);

    if (result.verdict === 'hard_reject') overall = 'hard_reject';
    else if (result.verdict === 'soft_flag' && overall === 'clean') overall = 'soft_flag';
  }

  // ── Duplicate session check: same lift logged 10+ times in one workout ──
  const liftCounts = new Map<string, number>();
  for (const e of entries) {
    liftCounts.set(e.liftKey, (liftCounts.get(e.liftKey) || 0) + 1);
  }
  for (const [key, count] of liftCounts) {
    if (count > 10) {
      log.warn('Excessive sets for single lift', { liftKey: key, count });
      if (overall === 'clean') overall = 'soft_flag';
    }
  }

  return { overall, results };
}
